import { Col, Switch } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { icons } from '../../../components/custom_components/Icon/icons'
import { IconIndicator, IconIndicatorBackground } from '../../../components/custom_components/IconIndicator/IconIndicator'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { PerInfoBoxDivider } from '../../../components/custom_components/InfoBox/InfoBoxDivider'
import { PerModal } from '../../../components/custom_components/Modal'
import PerInputNumber from '../../../components/custom_components/PerInputNumber'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'
import { updateRankingSeason } from '../../../graphql/talents-season/talentsSeasonMutations'
import {
  EmployeeTagType,
  EmployeeWithRankAndTags,
  RoleName,
  TalentsSeasonInput,
  TalentsSeasonSettingsDto,
  TalentsSeasonStatus,
} from '../../../types'
import { rankingUiProps } from '../../cultivatingTalents/ranking.uiprops'
import { ImpactOfLossSection, NumberSection, PerformanceSection, RiskOfLossSection, SettingsSection } from './InfoBoxSections'
import { useAuth } from '../../../context/auth-context'
import { showNotification } from '../../../components/custom_components/Notification/notification'

interface RankingEnabledInfoBoxProps {
  isHRSettings: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
  settings: TalentsSeasonSettingsDto
  infoBoxData: EmployeeWithRankAndTags[]
  previousSeasonExist: boolean
  setDisclaimerClosed: (value: boolean) => void
}

export const RankingEnabledInfoBox: FC<RankingEnabledInfoBoxProps> = ({
  isHRSettings,
  isAllCompaniesSettings,
  isYourCompanyView,
  settings,
  infoBoxData,
  previousSeasonExist,
  setDisclaimerClosed,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [rankingSettingsModalVisible, setRankingSettingsModalVisible] = useState<boolean>(false)

  const { isPerformanceTag, isImpactOfLossTag, isRiskOfLossTag } = rankingUiProps()

  const totalOfEmployees = infoBoxData.length

  const availableEmployeeTags = Object.keys(EmployeeTagType).map((key) => {
    return {
      tagName: EmployeeTagType[key],
      value: infoBoxData.filter((e) => e.tags.filter((t) => t.type === EmployeeTagType[key]).length > 0).length,
    }
  })

  const performanceUntaggedCount = infoBoxData.filter(
    (e) => e.tags.filter((t) => isPerformanceTag(t.type as EmployeeTagType)).length === 0,
  ).length

  const impactOfLossUntaggedCount = infoBoxData.filter(
    (e) => e.tags.filter((t) => isImpactOfLossTag(t.type as EmployeeTagType)).length === 0,
  ).length

  const riskOfLossUntaggedCount = infoBoxData.filter(
    (e) => e.tags.filter((t) => isRiskOfLossTag(t.type as EmployeeTagType)).length === 0,
  ).length

  const lowCounts =
    availableEmployeeTags.filter((t) => t.tagName === EmployeeTagType.Low)[0].value +
    availableEmployeeTags.filter((t) => t.tagName === EmployeeTagType.EntryLevelLow)[0].value

  const minLowTagsNecessary = !!settings.lowPercentage ? Math.round((settings.lowPercentage * totalOfEmployees) / 100) : 0

  const topCounts = availableEmployeeTags.filter((t) => t.tagName === EmployeeTagType.Top)[0].value

  const maxTopTagsNecessary = !!settings.topPercentage ? Math.round((settings.topPercentage * totalOfEmployees) / 100) : 0

  const hasEntryLevelLowTag = infoBoxData.some((employee) =>
    employee.tags.some((tag) => tag.type === EmployeeTagType.EntryLevelLow),
  )

  return (
    <>
      <RankingSettingsModal
        employeesCount={totalOfEmployees}
        previousSeasonExist={previousSeasonExist}
        isOpen={rankingSettingsModalVisible}
        settings={settings}
        hasEntryLevelLowTag={hasEntryLevelLowTag}
        onClose={() => {
          setRankingSettingsModalVisible(false)
        }}
      />
      <div style={{ overflowX: 'auto' }}>
        <PerInfoBox mb="xxs" scrollX>
          <NumberSection totalOfEmployees={totalOfEmployees} />
          <PerInfoBoxDivider />
          <SettingsSection
            isHRSettings={isHRSettings}
            isYourCompanyView={isYourCompanyView}
            isAllCompaniesSettings={isAllCompaniesSettings}
            onCompanySettingsClick={() => {
              setRankingSettingsModalVisible(true)
            }}
          />
          <PerInfoBoxDivider />
          <PerformanceSection
            isRankingEnabled
            minLowTag={minLowTagsNecessary}
            maxTopTag={maxTopTagsNecessary}
            availableEmployeeTags={availableEmployeeTags}
            lowCounts={lowCounts}
            topCounts={topCounts}
            performanceUntaggedCount={performanceUntaggedCount}
          />
          <PerInfoBoxDivider />
          <ImpactOfLossSection availableEmployeeTags={availableEmployeeTags} untaggedCount={impactOfLossUntaggedCount} />
          <PerInfoBoxDivider />
          <RiskOfLossSection availableEmployeeTags={availableEmployeeTags} untaggedCount={riskOfLossUntaggedCount} />
        </PerInfoBox>
      </div>
      {(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
        [TalentsSeasonStatus.Opened].includes(settings.talentsSeasonStatus) && (
          <RankingDisclaimer
            message={t('companySettingsView.rankingTab.rankingDisclaimer.openedRankingMessageForHr', { year: settings.year })}
            description={t('companySettingsView.rankingTab.rankingDisclaimer.openedRankingDescriptionForHr', { year: settings.year })}
            iconColor="warning"
            icon="warning_fill"
            setDisclaimerClosed={setDisclaimerClosed}
          />
        )}
      {!(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
        [TalentsSeasonStatus.Opened].includes(settings.talentsSeasonStatus) && (
          <RankingDisclaimer
            message={
              user.roles && user.roles.includes(RoleName.BusinessPartner)
                ? t('companySettingsView.rankingTab.rankingDisclaimer.openedRankingMessageForBusinessPartner')
                : t('companySettingsView.rankingTab.rankingDisclaimer.openedRankingMessage')
            }
            description={t('companySettingsView.rankingTab.rankingDisclaimer.openedRankingDescription')}
            iconColor="warning"
            icon="warning_fill"
            setDisclaimerClosed={setDisclaimerClosed}
          />
        )}
      {!(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
        [TalentsSeasonStatus.Confirmed].includes(settings.talentsSeasonStatus) && (
          <RankingDisclaimer
            icon="check_stroke"
            message={t('companySettingsView.rankingTab.rankingDisclaimer.confirmedRankingMessage')}
            description={t('companySettingsView.rankingTab.rankingDisclaimer.confirmedRankingDescription')}
            iconColor="success"
            setDisclaimerClosed={setDisclaimerClosed}
          />
        )}
    </>
  )
}

const RankingDisclaimer: FC<{
  message: string
  description: string
  iconColor: IconIndicatorBackground
  icon: keyof typeof icons
  setDisclaimerClosed: (value: boolean) => void
}> = ({ message, description, icon, iconColor, setDisclaimerClosed }) => {
  const [hidden, setHidden] = useState(false)

  return (
    <BackgroundBox mb="xxs" r height="messageBox" noPadding hidden={hidden} color="gray-3">
      <PerFlexRow align="middle" noWrap className="fullHeight" justify="space-between" mr="l" ml="l">
        <PerFlexRow direction="row" align="middle">
          <PerFlexRow>
            <IconIndicator icon={icon} background={iconColor} />
          </PerFlexRow>
          <PerSpacer ml="xs" mr="xs" />
          <PerFlexRow direction="column">
            <PerText fontSize="m" color="default" mb="xxxs">
              {message}
            </PerText>
            <PerText color="secondary" fontSize="m">
              {description}
            </PerText>
          </PerFlexRow>
        </PerFlexRow>
        <PerFlexRow direction="row">
          <PerButtonCircle
            size="large"
            icon="close_stroke"
            onClick={() => {
              setHidden(true)
              setDisclaimerClosed(true)
            }}
          />
        </PerFlexRow>
      </PerFlexRow>
    </BackgroundBox>
  )
}

const RankingSettingsModal: FC<{
  employeesCount: number
  previousSeasonExist: boolean
  settings: TalentsSeasonSettingsDto
  isOpen: boolean
  hasEntryLevelLowTag: boolean
  onClose: () => void
}> = ({ employeesCount, previousSeasonExist, settings, isOpen, hasEntryLevelLowTag, onClose }) => {
  const { t } = useTranslation()
  const [percentageOfLow, setPercentageOfLow] = useState<number | undefined>(settings.lowPercentage)
  const [percentageOfTop, setPercentageOfTop] = useState<number | undefined>(settings.topPercentage)
  const [allowChanges, setAllowChanges] = useState<boolean>(!settings.blockedChangesForLow)
  const [isSaving, toggleSaving] = useState<boolean>(false)
  const [errorMessageVisible, setErrorMessageVisible] = useState<{ errors: boolean; updateOk: boolean | null }>({
    errors: false,
    updateOk: true,
  })
  const [reasonForEditRanking, setReasonForEditRanking] = useState<string>('')

  const clearRankingModal = () => {
    setPercentageOfLow(settings.lowPercentage)
    setPercentageOfTop(settings.topPercentage)
    setAllowChanges(!settings.blockedChangesForLow)
    setReasonForEditRanking('')
    setErrorMessageVisible({ errors: false, updateOk: true })
  }

  const updateRanking = async (): Promise<{ updateOk: boolean | null; errors: boolean }> => {
    toggleSaving(true)
    const talentsSeasonInput: TalentsSeasonInput = {
      id: settings.talentsSeasonId ? settings.talentsSeasonId : undefined,
      year: settings.year,
      companyId: settings.companyId,
      country: settings.country,
      talentsSeasonStatus: TalentsSeasonStatus.Opened,
      lowPercentage: percentageOfLow,
      topPercentage: percentageOfTop,
      blockedChangesForLow: !allowChanges,
      reason: reasonForEditRanking,
    }
    // This function returns false when the ranking report generation is in progress or failed
    const { data, errors } = await updateRankingSeason(talentsSeasonInput)
    toggleSaving(false)
    return !!errors || !data ? { errors: true, updateOk: null } : { errors: false, updateOk: !!data.updateRankingSeason }
  }

  const isSeasonReady = settings.talentsSeasonStatus === TalentsSeasonStatus.Ready

  return (
    <>
      <PerModal
        destroyOnClose
        size="small"
        title={{
          heading: isSeasonReady
            ? t('companySettingsView.rankingTab.rankingSettingsModal.openRankingHeading')
            : t('companySettingsView.rankingTab.rankingSettingsModal.editRankingHeading'),
        }}
        centered
        visible={isOpen}
        onCancel={() => {
          onClose()
          clearRankingModal()
        }}
        footer={{
          cancelBtn: {
            onClick: () => {
              onClose()
              clearRankingModal()
            },
          },
          customBtns: [
            {
              text: isSeasonReady
                ? t('companySettingsView.rankingTab.rankingSettingsModal.openRanking')
                : t('companySettingsView.rankingTab.rankingSettingsModal.saveChanges'),
              onClick: async () => {
                const { updateOk, errors } = await updateRanking()
                if (!!updateOk) {
                  setReasonForEditRanking('')
                  onClose()
                  const operation = isSeasonReady ? 'opened' : 'edited'
                  showNotification({
                    type: 'success',
                    message: t(`companySettingsView.rankingTab.rankingSettingsModal.notifications.success.title`),
                    description: t(`companySettingsView.rankingTab.rankingSettingsModal.notifications.success.description`, { operation }),
                  })
                } else {
                  setErrorMessageVisible({ errors, updateOk })
                }
              },
              disabled: isSeasonReady
                ? percentageOfLow === null || percentageOfTop === null
                : percentageOfLow === null || percentageOfTop === null || !reasonForEditRanking,
              color: 'success',
              icon: 'check_stroke',
              isProcessing: { active: isSaving },
            },
          ],
        }}
      >
        <PerFlexRow mb="xs" mt="s">
          <PerText fontSize="m" fontWeight="bold">
            {t('companySettingsView.rankingTab.rankingSettingsModal.percentageOfLow')}
          </PerText>
        </PerFlexRow>
        <PerFlexRow align="middle">
          <Col span={8}>
            <PerInputNumber
              value={percentageOfLow}
              type="percentage"
              min={0}
              max={100}
              step={10}
              onChange={(value) => {
                setPercentageOfLow(value)
              }}
            />
          </Col>
          {!isSeasonReady && (
            <Col span={16}>
              <PerText fontSize="xs" color="secondary">
                {t('companySettingsView.rankingTab.rankingSettingsModal.numberOfEmployees')}
                <PerText display="inline" fontSize="xs" fontWeight="bold" color="default">
                  {!!percentageOfLow ? Math.round((percentageOfLow * employeesCount) / 100) : 0}
                </PerText>
              </PerText>
            </Col>
          )}
        </PerFlexRow>

        <PerFlexRow mb="xs" mt="xxl">
          <PerText fontSize="m" fontWeight="bold">
            {t('companySettingsView.rankingTab.rankingSettingsModal.percentageOfTop')}
          </PerText>
        </PerFlexRow>
        <PerFlexRow align="middle">
          <Col span={8}>
            <PerInputNumber
              value={percentageOfTop}
              type="percentage"
              min={0}
              max={100}
              step={10}
              onChange={(value) => {
                setPercentageOfTop(value)
              }}
            />
          </Col>
          {!isSeasonReady && (
            <Col span={16}>
              <PerText fontSize="xs" color="secondary">
                {t('companySettingsView.rankingTab.rankingSettingsModal.numberOfEmployees')}
                <PerText display="inline" fontSize="xs" fontWeight="bold" color="default">
                  {!!percentageOfTop ? Math.round((percentageOfTop * employeesCount) / 100) : 0}
                </PerText>
              </PerText>
            </Col>
          )}
        </PerFlexRow>

        <PerFlexRow mb="xs" mt="xxl">
          <PerText fontSize="m" fontWeight="bold">
            {t('companySettingsView.rankingTab.rankingSettingsModal.contractChanges')}
          </PerText>
        </PerFlexRow>
        <PerFlexRow mb="xxl" justify="space-between" align="middle">
          <Col span={21}>
            <PerText fontSize="s">{t('companySettingsView.rankingTab.rankingSettingsModal.contractChangesDescription')}</PerText>
            {hasEntryLevelLowTag && (
              <PerText fontSize="xs" color="secondary" mt="xxxs">
                {t('companySettingsView.rankingTab.rankingSettingsModal.contractChangesDisabledDueToEntryLevelLow')}
              </PerText>
            )}
          </Col>
          <Col span={3}>
            <Switch
              checked={allowChanges}
              disabled={hasEntryLevelLowTag}
              onChange={(value) => {
                setAllowChanges(value)
              }}
            />
          </Col>
        </PerFlexRow>

        {!isSeasonReady && (
          <>
            <PerFlexRow mb="xs" mt="xxl">
              <PerText fontSize="m" fontWeight="bold">
                {t('companySettingsView.rankingTab.rankingSettingsModal.reason')}
              </PerText>
            </PerFlexRow>
            <TextArea
              value={reasonForEditRanking}
              onChange={(e) => {
                e.preventDefault()
                setReasonForEditRanking(e.target.value)
              }}
              placeholder={t('companySettingsView.rankingTab.rankingSettingsModal.reasonPlaceholder')}
              rows={1}
            />
            <PerSpacer mt="xxl" />
          </>
        )}

        {!!errorMessageVisible.errors ? (
          <PerText color="error">{t('companySettingsView.rankingTab.rankingSettingsModal.rankingConfirmationError')}</PerText>
        ) : !errorMessageVisible.updateOk ? (
          <PerText color="error">
            {t('companySettingsView.rankingTab.rankingSettingsModal.rankingReportGenerationInProgressOrFailed')}
          </PerText>
        ) : (
          <>
            {isSeasonReady ? (
              <>
                <PerText>{t('companySettingsView.rankingTab.rankingSettingsModal.disclaimer1', { year: settings.year })}</PerText>
                {previousSeasonExist && (
                  <PerText>{t('companySettingsView.rankingTab.rankingSettingsModal.disclaimer2', { year: settings.year - 1 })}</PerText>
                )}
              </>
            ) : (
              <PerText>{t('companySettingsView.rankingTab.rankingSettingsModal.disclaimer4', { year: settings.year })}</PerText>
            )}
            <PerText>{t('companySettingsView.rankingTab.rankingSettingsModal.disclaimer3')}</PerText>
          </>
        )}
      </PerModal>
    </>
  )
}
